/**
 * API Service - High-level API operations
 *
 * Provides domain-specific API functions using the centralized apiClient.
 * Separated into logical groupings: User, Journey, YearReview, Share
 */
import { apiClient } from './apiClient'
import type {
  ExerciseType,
  HoroscopeData,
  WordsData,
  VisionData,
  PhotoMemoriesData,
  Intention,
  ActionPlan,
  SummaryCardData,
  YearReviewData,
  YearReviewInsights,
  ThemeDiscoveryData,
  ThemeSynthesis,
  VisionSynthesis,
} from '../types'

// ============================================================
// Type Definitions
// ============================================================

interface User {
  id: number
  name: string
  created_at: string
  updated_at: string
}

export interface JourneyData {
  horoscope?: HoroscopeData | null
  theme?: ThemeDiscoveryData | null
  words?: WordsData | null
  vision?: VisionData | null
  photos?: PhotoMemoriesData | null
  completedExercises?: ExerciseType[]
  intentions?: Intention[]
  actionPlans?: ActionPlan[]
  summaryCard?: SummaryCardData | null
  wrappedData?: unknown // AI-generated wrapped content (WrappedData from store)
  yearReview?: YearReviewData | null
  review?: {
    reviewedAt: string
    reflections: string[]
    carriedForward: string[]
  } | null
}

interface LoginResponse {
  user: User
  yearsWithData: number[]
}

interface JourneyResponse {
  journey: JourneyData | null
}

interface YearReviewSynthesisResponse {
  insights: YearReviewInsights
  fallback?: boolean
  error?: string
}

export interface ShareData {
  name: string
  theme?: string | ThemeDiscoveryData | WordsData | null
  themeSynthesis?: ThemeSynthesis | null
  intentions: Intention[]
  actionPlans?: ActionPlan[]
  horoscope?: HoroscopeData | null
  words?: WordsData | null
  vision?: VisionData | null
  visionSynthesis?: VisionSynthesis | null
  photos?: PhotoMemoriesData | null
  yearReview?: YearReviewData | null
}

interface CreateShareResponse {
  shareId: string
  error?: string
}

interface GetShareResponse {
  shareId: string
  year: number
  data: ShareData
  createdAt: string
  error?: string
}

// ============================================================
// User API
// ============================================================

export async function loginOrCreateUser(name: string): Promise<LoginResponse | null> {
  const result = await apiClient.post<LoginResponse>('/api/user', { name })

  if (!result.success) {
    console.error('Failed to login/create user:', result.error)
    return null
  }

  return result.data
}

// ============================================================
// Journey API
// ============================================================

export async function getJourney(userId: number, year: number): Promise<JourneyData | null> {
  const result = await apiClient.get<JourneyResponse>(`/api/journey?userId=${userId}&year=${year}`)

  if (!result.success) {
    console.error('Failed to fetch journey:', result.error)
    return null
  }

  return result.data.journey
}

export async function saveJourney(
  userId: number,
  year: number,
  data: JourneyData
): Promise<boolean> {
  const result = await apiClient.post('/api/journey', { userId, year, data })

  if (!result.success) {
    console.error('Failed to save journey:', result.error)
    return false
  }

  return true
}

// Debounced save state - merges data from multiple stores
let saveTimeout: ReturnType<typeof setTimeout> | null = null
let pendingSave: { userId: number; year: number; data: JourneyData } | null = null

export function debouncedSaveJourney(
  userId: number,
  year: number,
  data: JourneyData,
  delay = 1000
): void {
  // Merge with existing pending data instead of overwriting
  if (pendingSave && pendingSave.userId === userId && pendingSave.year === year) {
    // Merge new data with existing pending data (new data takes precedence)
    pendingSave.data = { ...pendingSave.data, ...data }
  } else {
    pendingSave = { userId, year, data }
  }

  if (saveTimeout) {
    clearTimeout(saveTimeout)
  }

  saveTimeout = setTimeout(async () => {
    if (pendingSave) {
      await saveJourney(pendingSave.userId, pendingSave.year, pendingSave.data)
      pendingSave = null
    }
    saveTimeout = null
  }, delay)
}

export async function flushPendingSave(): Promise<void> {
  if (saveTimeout) {
    clearTimeout(saveTimeout)
    saveTimeout = null
  }
  if (pendingSave) {
    await saveJourney(pendingSave.userId, pendingSave.year, pendingSave.data)
    pendingSave = null
  }
}

export async function deleteJourney(userId: number, year?: number): Promise<boolean> {
  const url = year
    ? `/api/journey?userId=${userId}&year=${year}`
    : `/api/journey?userId=${userId}`

  const result = await apiClient.delete(url)

  if (!result.success) {
    console.error('Failed to delete journey:', result.error)
    return false
  }

  return true
}

// ============================================================
// Year Review Synthesis API
// ============================================================

export async function synthesizeYearReview(
  name: string,
  yearReview: YearReviewData,
  year: number
): Promise<YearReviewInsights | null> {
  const result = await apiClient.postAI<YearReviewSynthesisResponse>(
    '/api/year-review',
    { name, yearReview, year }
  )

  if (!result.success) {
    console.error('Failed to synthesize year review:', result.error)
    return null
  }

  return result.data.insights
}

// ============================================================
// Share API
// ============================================================

export async function createShare(
  data: ShareData,
  year: number
): Promise<string | null> {
  const result = await apiClient.post<CreateShareResponse>('/api/share', { data, year })

  if (!result.success) {
    console.error('Failed to create share:', result.error)
    return null
  }

  return result.data.shareId
}

export async function getShare(shareId: string): Promise<GetShareResponse | null> {
  const result = await apiClient.get<GetShareResponse>(`/api/share?id=${shareId}`)

  if (!result.success) {
    console.error('Failed to get share:', result.error)
    return null
  }

  return result.data
}
